import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, XCircle, Clock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

export default function OrderPending() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { clearCart } = useCart();
  const sessionId = params.get("session");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout

  useEffect(() => {
    document.title = "Confirming payment – Cheap4Teach";
    if (!sessionId) { nav("/"); return; }

    let attempts = 0;
    const MAX = 12; // 24 seconds total

    const poll = setInterval(async () => {
      attempts++;
      try {
        const r = await api.get(`/wipay/session/${sessionId}`);
        const { status, order_id } = r.data;
        if (status === "paid" && order_id) {
          clearInterval(poll);
          clearCart();
          nav(`/order-success?id=${order_id}`);
        } else if (status === "failed") {
          clearInterval(poll);
          setState("failed");
        } else if (attempts >= MAX) {
          clearInterval(poll);
          setState("timeout");
        }
      } catch {
        if (attempts >= MAX) {
          clearInterval(poll);
          setState("timeout");
        }
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === "failed") {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="c4t-card">
          <XCircle className="w-16 h-16 text-[#FF477E] mx-auto mb-4" strokeWidth={2} />
          <h1 className="text-3xl font-['Fredoka'] font-bold">Card payment declined</h1>
          <p className="c4t-desc mt-2">Your card was not charged. Your cart has been saved. You can try again or choose Bank Transfer.</p>
          <Link to="/checkout" className="mt-6 inline-flex c4t-btn c4t-btn-pink">Try again</Link>
          <Link to="/cart" className="block mt-3 text-[#118AB2] hover:text-[#FF477E] text-sm">← Back to cart</Link>
        </div>
      </div>
    );
  }

  if (state === "timeout") {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="c4t-card">
          <Clock className="w-16 h-16 text-[#FFC500] mx-auto mb-4" strokeWidth={2} />
          <h1 className="text-3xl font-['Fredoka'] font-bold">Still processing…</h1>
          <p className="c4t-desc mt-2">If your payment went through, a confirmation email is on its way. If you don't receive one within a few minutes, please contact us.</p>
          <Link to="/" className="mt-6 inline-flex c4t-btn c4t-btn-pink">Go home</Link>
        </div>
      </div>
    );
  }

  // Default: polling spinner
  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center">
      <div className="c4t-card">
        <Loader2 className="w-16 h-16 text-[#00A651] mx-auto mb-4 animate-spin" strokeWidth={2} />
        <h1 className="text-3xl font-['Fredoka'] font-bold">Confirming your payment…</h1>
        <p className="c4t-desc mt-2">Please don't close this page. We're waiting for confirmation from WiPay.</p>
      </div>
    </div>
  );
}
